'use client';

import Link from '@/components/i18n/LocaleLink';
import Image from 'next/image';
import { useEffect, useState } from 'react';

type Lang = 'en' | 'es' | 'pt' | 'de' | 'fr';

const legalTranslations: Record<Lang, { backToHome: string; copyright: string }> = {
  en: { backToHome: '← Back to home', copyright: 'All rights reserved.' },
  es: { backToHome: '← Volver al inicio', copyright: 'Todos los derechos reservados.' },
  pt: { backToHome: '← Voltar ao início', copyright: 'Todos os direitos reservados.' },
  de: { backToHome: '← Zurück zur Startseite', copyright: 'Alle Rechte vorbehalten.' },
  fr: { backToHome: '← Retour à l\'accueil', copyright: 'Tous droits réservés.' },
};

export default function LegalLayout({ children }: { children: React.ReactNode }) {
  const [lang, setLang] = useState<Lang>('en');

  useEffect(() => {
    // Locale cascade shared with the rest of the app:
    //   1. ezdocu-locale cookie (set by the landing selector and by server
    //      pages via getLocale). Wins so a deliberate pick carries across.
    //   2. localStorage['lang'] legacy — kept in sync by the landing too.
    //   3. browser Accept-Language fallback.
    const cookieMatch = typeof document !== 'undefined'
      ? document.cookie.match(/ezdocu-locale=([^;]+)/)
      : null;
    const fromCookie = cookieMatch?.[1];
    if (fromCookie && ['en', 'es', 'pt', 'de', 'fr'].includes(fromCookie)) {
      setLang(fromCookie as Lang);
      return;
    }
    const saved = localStorage.getItem('lang') as Lang;
    if (saved && ['en', 'es', 'pt', 'de', 'fr'].includes(saved)) {
      setLang(saved);
      return;
    }
    const browserLang = navigator.language?.split('-')[0] || 'en';
    if (['es', 'pt', 'de', 'fr'].includes(browserLang)) {
      setLang(browserLang as Lang);
    }
  }, []);

  const t = legalTranslations[lang];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 px-4 py-3 sticky top-0 bg-white/95 backdrop-blur-sm z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <Image
              src="/images/brand/ezdocuai-logo.png"
              alt="EZDocuAI"
              width={36}
              height={36}
              className="h-8 w-8"
            />
            <span className="text-lg font-semibold text-gray-900">EZDocuAI</span>
          </Link>
          <Link href="/" className="text-sm text-purple-600 hover:text-purple-800 transition-colors font-medium">
            {t.backToHome}
          </Link>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1">
        {children}
      </div>

      {/* Minimal footer */}
      <footer className="border-t border-gray-200 py-6 px-4">
        <div className="max-w-7xl mx-auto text-center text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} EZDocuAI, Inc. {t.copyright}</p>
        </div>
      </footer>
    </div>
  );
}
